import {SnackbarProvider} from 'notistack';
import {Suspense, lazy} from 'react';
import {QueryClient, QueryClientProvider} from 'react-query';
import {ReactQueryDevtools} from 'react-query/devtools';
import './App.css';
import ScrollToTop from '@/components/ScrollToTop.tsx';
import {LocalizationProvider} from '@mui/x-date-pickers';
import {AdapterMoment} from '@mui/x-date-pickers/AdapterMoment';
import {
    Outlet,
    RouterProvider,
    createRootRoute,
    createRoute,
    createRouter,
    redirect,
} from '@tanstack/react-router';
import LandingPage from './components/Pages/LandingPage';
import ErrorBoundary from './components/ErrorBoundary';
import NotFound404 from './components/Pages/NotFound404';
import {ScheduledMaintenance} from './components/Pages/ScheduledMaintenance';
import SignOut from './components/Pages/SignOut';
import JWTProvider from './components/Providers/JWTProvider';

const AdminEnrollmentDashboard = lazy(
    async () => import('./components/Pages/AdminEnrollmentDashboard'),
);
const PdfViewerPage = lazy(async () => import('./components/Pages/PdfViewerPage'));
const DownloadPdf = lazy(async () => import('./components/Pages/DownloadPdf'));
const StudentEnrollment = lazy(async () => import('./components/Pages/StudentEnrollment'));
const EnrollmentDashboard = lazy(async () => import('./components/Pages/EnrollmentDashboard'));

const rootRoute = createRootRoute({
    component: () => (
        <>
            <ScrollToTop />
            <ScheduledMaintenance>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                    <Outlet />
                </LocalizationProvider>
            </ScheduledMaintenance>
        </>
    ),
});

const indexRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/',
    component: LandingPage,
});

const signInRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/signin',
    beforeLoad: () => {
        throw redirect({to: '/admin/dashboard'});
    },
});

const suspensefulRoutes = createRoute({
    getParentRoute: () => rootRoute,
    id: 'suspense-routes',
    component: () => (
        <Suspense fallback={<div>Loading...</div>}>
            <Outlet />
        </Suspense>
    ),
});

const enrollmentDashRoute = createRoute({
    getParentRoute: () => suspensefulRoutes,
    path: '/dashboard/$guardianId',
    component: EnrollmentDashboard,
});

const enrollmentDashRouteFl = createRoute({
    getParentRoute: () => enrollmentDashRoute,
    path: '/$firstLoad',
    component: EnrollmentDashboard,
});

const enrollmentDashRouteLc = createRoute({
    getParentRoute: () => enrollmentDashRouteFl,
    path: '/$languageCode',
    component: EnrollmentDashboard,
});

const enrollmentRoute = createRoute({
    getParentRoute: () => suspensefulRoutes,
    path: '/enrollment/$enrollmentId',
    component: StudentEnrollment,
});

const enrollmentStepRoute = createRoute({
    getParentRoute: () => enrollmentRoute,
    path: '/$step',
    component: StudentEnrollment,
});

const viewPdfRoute = createRoute({
    getParentRoute: () => suspensefulRoutes,
    path: '/view-pdf/$enrollmentId',
    component: PdfViewerPage,
});

const viewPdfLcRoute = createRoute({
    getParentRoute: () => viewPdfRoute,
    path: '/$languageCode',
    component: PdfViewerPage,
});

const adminRoutes = createRoute({
    getParentRoute: () => suspensefulRoutes,
    path: '/admin',
    component: () => (
        <JWTProvider>
            <Outlet />
        </JWTProvider>
    ),
});

const adminIndexRoute = createRoute({
    getParentRoute: () => adminRoutes,
    path: '/',
    beforeLoad: () => {
        throw redirect({to: '/admin/dashboard'});
    },
});

const signOutRoute = createRoute({
    getParentRoute: () => adminRoutes,
    path: '/signout',
    component: SignOut,
});

const adminDashboardRoute = createRoute({
    getParentRoute: () => adminRoutes,
    path: '/dashboard',
    component: AdminEnrollmentDashboard,
});

const adminPdfRoute = createRoute({
    getParentRoute: () => adminRoutes,
    path: '/pdf/$enrollmentId/$languageCode',
    component: DownloadPdf,
});

const adminPdfTypeRoute = createRoute({
    getParentRoute: () => adminPdfRoute,
    path: '/$type',
    component: DownloadPdf,
});

const routeTree = rootRoute.addChildren([
    indexRoute,
    signInRoute,
    suspensefulRoutes.addChildren([
        enrollmentDashRoute.addChildren([
            enrollmentDashRouteFl.addChildren([enrollmentDashRouteLc]),
        ]),
        enrollmentRoute.addChildren([enrollmentStepRoute]),
        viewPdfRoute.addChildren([viewPdfLcRoute]),
        adminRoutes.addChildren([
            adminIndexRoute,
            signOutRoute,
            adminDashboardRoute,
            adminPdfRoute.addChildren([adminPdfTypeRoute]),
        ]),
    ]),
]);

const appRouter = createRouter({
    routeTree: routeTree,
    defaultNotFoundComponent: NotFound404,
});

const App = () => {
    return (
        <ErrorBoundary>
            <SnackbarProvider maxSnack={3}>
                <QueryClientProvider
                    client={
                        new QueryClient({
                            defaultOptions: {
                                queries: {
                                    retry: 1,
                                    refetchOnWindowFocus: false,
                                },
                            },
                        })
                    }
                >
                    <RouterProvider router={appRouter} />
                    <ReactQueryDevtools initialIsOpen={false} />
                </QueryClientProvider>
            </SnackbarProvider>
        </ErrorBoundary>
    );
};

export default App;
